/**
 * What a refused reader is told and how they ask for more, the scoped-view
 * hint, and the shell's organisation switcher, launcher and account link.
 * The same words as the admin portal's, so the two apps say one thing.
 */
export const forbidden = {
  title: 'You do not have access to this',
  page: 'This needs access across the whole organisation, and yours is limited to some of it.',
  general: 'Your access does not include this.',
  request: 'Request access',
}

export const requestAccess = {
  title: 'Request access',
  toOrganisation: 'Ask for the access you need. The people who can give it are emailed.',
  where: 'Where',
  wholeOrganisation: 'The whole organisation',
  kinds: {
    store: 'A store',
    brand: 'A brand',
    marketplace: 'A marketplace',
    warehouse: 'A warehouse',
    retailer: 'A retailer',
    service_provider: 'A service provider',
    vendor: 'A vendor',
  },
  place: 'Which one',
  placeHint_one: 'Type at least {{count}} letter of its name.',
  placeHint_other: 'Type at least {{count}} letters of its name.',
  searching: 'Searching…',
  noPlaces: 'Nothing here matches that name.',
  pickPlace: 'Choose one of the matches.',
  matches: 'Matching places',
  changePlace: 'Change',
  what: 'What you need',
  choose: 'Choose a role or a permission',
  roles: 'Roles',
  permissions: 'Permissions',
  loading: 'Loading what can be asked for…',
  unavailable: 'The list of roles and permissions could not be loaded. Try again later.',
  reason: 'Why you need it',
  reasonHint: 'Shown to whoever decides. Required.',
  cancel: 'Cancel',
  submit: 'Send request',
  submitting: 'Sending…',
  sentTitle: 'Request sent',
  sent: 'Whoever decides has been emailed. You will hear back by email when they do.',
  close: 'Done',
  errors: {
    what: 'Choose what you need.',
    place: 'Choose the place you need it on.',
    reason: 'Say why you need it.',
    reasonTooLong: 'Keep the reason to {{max}} characters.',
  },
  alreadyPending: 'You have already asked for this, and it is waiting for a decision.',
  alreadyGranted: 'You already have this. Sign in again if it does not show yet.',
}

// "Showing 3 stores you can access" — on a page whose reader holds it only in part.
export const scoped = {
  store_one: 'Showing {{count}} store you can access.',
  store_other: 'Showing {{count}} stores you can access.',
  hint: 'Your access is limited to some of this organisation.',
}

export const shell = {
  switchOrganisation: 'Switch organisation',
  switching: 'Switching…',
  switchFailed: 'Could not switch organisation: {{reason}}',
  current: 'Current',
  apps: 'Apps',
  appsLabel: 'Open another VentaWiz app',
  appsUnavailable: 'The list of apps could not be loaded.',
  appsNone: 'No other apps are available.',
  accountSettings: 'Account settings',
}

// "My access": one's own access in this organisation, and one's requests (VentaWiz-Auth ADR 0022).
const KINDS = {
  store: 'Stores',
  brand: 'Brands',
  marketplace: 'Marketplaces',
  warehouse: 'Warehouses',
  retailer: 'Retailers',
  service_provider: 'Service providers',
  vendor: 'Vendors',
}

export const myAccess = {
  title: 'My access',
  description: 'What you can open in this organisation, and how to ask for more.',
  current: 'Your access',
  emptyTitle: 'You do not have access to anything in {{organisation}} yet',
  emptyBody:
    'Ask for access to the stores, brands or warehouses you work on. Whoever can give it is emailed.',
  request: 'Request access',
  requestMore: 'Request more access',
  nothingHere: 'Nothing here yet.',
  organisationWide: 'Whole organisation',
  wholeOrganisation: 'The whole organisation',
  kinds: KINDS,
  allOf: {
    store: 'All stores',
    brand: 'All brands',
    marketplace: 'All marketplaces',
    warehouse: 'All warehouses',
    retailer: 'All retailers',
    service_provider: 'All service providers',
    vendor: 'All vendors',
  },
  unnamedPlace: 'Place {{id}}',
  admin: 'Admin',
  until: 'until {{date}}',
  requests: 'My requests',
  requestItem: '{{what}} on {{where}}',
  requestedOn: 'Asked on {{date}}',
  status: {
    pending: 'Waiting',
    approved: 'Approved',
    rejected: 'Rejected',
    cancelled: 'Cancelled',
  },
  cancel: 'Cancel',
  cancelFor: 'Cancel the request for {{what}}',
}
